/**
 * Builds API request from component state using schema to determine structure
 * This ensures we only include fields that exist in the API schema
 *
 * REFACTORED VERSION:
 * - Uses fieldMapping.ts for field mappings
 * - Uses extracted utilities (valueUtils, photoUtils, schemaUtils)
 * - Uses fieldExtractor for value extraction
 * - Uses sectionBuilders for complex array transformations
 */

import {
  WebsiteGeneratorState,
  FieldSchema,
  FieldSchemaValue,
} from "../../../types/staticWebGenerator";
import { hasValue, getValue } from "./valueUtils";
import {
  isFieldSchemaValue,
  isOptionalSection,
  getNestedSchema,
  getOptionalSectionEnabledKey,
} from "./schemaUtils";
import { extractFieldValue, isSectionEnabled } from "./fieldExtractor";

/**
 * Builds API request from component state using schema to determine structure
 *
 * @param state - Unified state from WebsiteGeneratorWizard
 * @param businessInfo - Business info from useSalon() context
 * @param schema - Field schema from populate-site-template response
 * @param optionalSectionEnabled - Optional section enabled states
 * @returns API request payload matching schema structure
 */
export const buildTemplateRequestFromSchema = (
  state: WebsiteGeneratorState,
  businessInfo: any,
  schema: FieldSchema | null,
  optionalSectionEnabled?: Record<string, boolean>,
): any => {
  if (!schema) {
    throw new Error(
      "Schema is required. Please reload the page to fetch the schema.",
    );
  }

  // Log state before constructing request object
  console.log(
    "=== buildTemplateRequestFromSchema - State Before Request Construction ===",
  );
  console.log("State:", JSON.stringify(state, null, 2));
  console.log("Business Info:", JSON.stringify(businessInfo, null, 2));
  console.log(
    "Optional Section Enabled:",
    JSON.stringify(optionalSectionEnabled, null, 2),
  );
  console.log("Schema Keys:", Object.keys(schema));
  console.log(
    "========================================================================",
  );

  const request: any = {};

  // Always include template_type and company_name (required for API)
  request.template_type = state.selectedTheme || "generic";
  const companyName = businessInfo?.salon_name || businessInfo?.name;
  if (companyName) {
    request.company_name = getValue(companyName) || companyName;
  }

  // Process each top-level section in schema
  for (const [schemaKey, schemaValue] of Object.entries(schema)) {
    // Skip already handled fields
    if (schemaKey === "template_type" || schemaKey === "company_name") {
      continue;
    }

    // Handle legacy google_maps top-level field
    if (schemaKey === "google_maps" && isFieldSchemaValue(schemaValue)) {
      if (state.contact?.showMap && hasBusinessAddress(businessInfo)) {
        request[schemaKey] = true;
      }
      continue;
    }

    // Get nested schema (handles both wrapped and plain formats)
    const nestedSchema = getNestedSchema(schemaValue);
    if (!nestedSchema) continue;

    // Special handling for color and font sections
    if (schemaKey === "color" || schemaKey === "font") {
      request[schemaKey] = buildStyleSection(schemaKey, state);
      continue;
    }

    // Build section from schema
    const sectionData = buildSection(
      schemaKey,
      nestedSchema,
      state,
      businessInfo,
      optionalSectionEnabled,
    );

    // Include section based on type (optional vs required)
    if (isOptionalSection(schemaKey)) {
      const enabled = isSectionEnabled(
        schemaKey,
        state,
        optionalSectionEnabled,
      );
      if (enabled) {
        request[schemaKey] = sectionData;
      }
    } else {
      // Required sections always included
      request[schemaKey] = sectionData;
    }
  }

  // Clean and return
  const cleaned = cleanRequest(request);
  const finalRequest = ensureRequiredFields(cleaned, state, companyName);

  // Log final request object
  console.log("=== buildTemplateRequestFromSchema - Final Request Object ===");
  console.log("Final Request:", JSON.stringify(finalRequest, null, 2));
  console.log(
    "========================================================================",
  );

  return finalRequest;
};

/**
 * Check if business has address info
 */
const hasBusinessAddress = (businessInfo: any): boolean => {
  return !!(
    businessInfo?.street_address_1 ||
    businessInfo?.city ||
    businessInfo?.state ||
    businessInfo?.zip_code
  );
};

/**
 * Build color/font style section
 */
const buildStyleSection = (
  schemaKey: "color" | "font",
  state: WebsiteGeneratorState,
): Record<string, string> => {
  if (schemaKey === "color") {
    return {
      primary_color: state.colors?.primary ?? "",
      secondary_color: state.colors?.secondary ?? "",
    };
  }
  return {
    primary_font: state.fonts?.heading ?? "",
    secondary_font: state.fonts?.body ?? "",
  };
};

/**
 * Build a section from schema
 */
const buildSection = (
  schemaKey: string,
  nestedSchema: FieldSchema,
  state: WebsiteGeneratorState,
  businessInfo: any,
  optionalSectionEnabled?: Record<string, boolean>,
): any => {
  const sectionData: any = {};

  for (const [fieldKey, fieldValue] of Object.entries(nestedSchema)) {
    if (isFieldSchemaValue(fieldValue)) {
      // Leaf field - extract value from state
      const value = extractFieldValue(schemaKey, fieldKey, state, businessInfo);
      if (value !== undefined) {
        sectionData[fieldKey] = value;
      } else {
        // Try schema default for required fields (non-optional sections only)
        if (!isOptionalSection(schemaKey)) {
          const schemaDefault = getSchemaDefault(fieldValue);
          if (schemaDefault !== undefined) {
            sectionData[fieldKey] = schemaDefault;
          }
        }
      }
    } else {
      // Nested object - build recursively or use array builder
      const nestedValue = extractFieldValue(
        schemaKey,
        fieldKey,
        state,
        businessInfo,
      );
      if (nestedValue !== undefined) {
        sectionData[fieldKey] = nestedValue;
      } else if (isOptionalSection(schemaKey)) {
        // For enabled optional sections, include empty arrays
        const enabled = isSectionEnabled(
          schemaKey,
          state,
          optionalSectionEnabled,
        );
        if (enabled && isArrayField(schemaKey, fieldKey)) {
          sectionData[fieldKey] = [];
        }
      }
    }
  }

  return sectionData;
};

/**
 * Get default value from schema for required fields
 */
const getSchemaDefault = (fieldValue: FieldSchemaValue): any => {
  if (!fieldValue.required) return undefined;
  const schemaDefault = fieldValue.value;
  if (
    schemaDefault === undefined ||
    schemaDefault === null ||
    (typeof schemaDefault === "string" && schemaDefault.trim() === "")
  ) {
    return undefined;
  }
  return schemaDefault;
};

/**
 * Check if a field is an array field
 */
const isArrayField = (schemaKey: string, fieldKey: string): boolean => {
  return (
    (schemaKey === "gallery" &&
      (fieldKey === "items" ||
        fieldKey === "images" ||
        fieldKey === "photos")) ||
    (schemaKey === "testimonials" &&
      (fieldKey === "testimonials" ||
        fieldKey === "items" ||
        fieldKey === "cards")) ||
    (schemaKey === "service_highlights" && fieldKey === "highlights")
  );
};

/**
 * Clean up empty values from request
 * Preserves empty arrays/objects for optional sections
 */
const cleanRequest = (obj: any): any => {
  if (obj === null || obj === undefined) return undefined;

  if (Array.isArray(obj)) {
    const cleaned = obj
      .map((item) => cleanRequest(item))
      .filter((item) => item !== null && item !== undefined);
    return cleaned;
  }

  if (typeof obj === "object") {
    const cleaned: any = {};
    for (const [key, value] of Object.entries(obj)) {
      if (value === null || value === undefined) continue;

      // Preserve optional section empty objects/arrays
      if (isOptionalSection(key)) {
        if (typeof value === "object") {
          cleaned[key] = cleanRequest(value) ?? {};
          continue;
        }
      }

      const cleanedValue = cleanRequest(value);
      if (cleanedValue === undefined) continue;

      // Skip empty objects (except for optional sections)
      if (
        typeof cleanedValue === "object" &&
        !Array.isArray(cleanedValue) &&
        Object.keys(cleanedValue).length === 0 &&
        !isOptionalSection(key)
      ) {
        continue;
      }

      cleaned[key] = cleanedValue;
    }
    return Object.keys(cleaned).length > 0 ? cleaned : undefined;
  }

  return obj;
};

/**
 * Ensure required fields are present
 */
const ensureRequiredFields = (
  cleaned: any,
  state: WebsiteGeneratorState,
  companyName: string | undefined,
): any => {
  if (!cleaned || Object.keys(cleaned).length === 0) {
    return {
      template_type: state.selectedTheme || "generic",
      ...(companyName ? { company_name: companyName } : {}),
    };
  }

  if (!cleaned.template_type) {
    cleaned.template_type = state.selectedTheme || "generic";
  }

  return cleaned;
};
